/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/



export const POSITION_RANGE = [0, 10000];
export const SIZE_RANGE = [1, 10000];
export const STACK_RANGE = [0, 9999];
export const REFERENCE_WIDTH_RANGE = [320, 4096];


export function snapToGrid(value, grid = 4) {
  return Math.round(value / grid) * grid;
}

export function constrainPosition(layout, sectionWidth, sectionHeight) {
  const { x, y, width, height } = layout;
  const maxX = Math.max(0, sectionWidth - width);
  const maxY = Math.max(0, sectionHeight - height);
  return {
    x: Math.min(Math.max(x, 0), maxX),
    y: Math.min(Math.max(y, 0), maxY),
  };
}

export function constrainSize(
  layout,
  sectionWidth,
  sectionHeight,
  minWidth = 32,
  minHeight = 24
) {
  const { x, y, width, height } = layout;
  const maxWidth = Math.max(minWidth, sectionWidth - x);
  const maxHeight = Math.max(minHeight, sectionHeight - y);
  return {
    width: Math.min(Math.max(width, minWidth), maxWidth),
    height: Math.min(Math.max(height, minHeight), maxHeight),
  };
}


export function alignmentPosition(mode, elementWidth, sectionWidth) {
  switch (mode) {
    case 'left':
      return 0;
    case 'center':
      return Math.round((sectionWidth - elementWidth) / 2);
    case 'right':
      return sectionWidth - elementWidth;
    default:
      throw new Error(`alignmentPosition: unknown mode "${mode}"`);
  }
}


export function nudge(position, key, withShift) {
  const { x, y } = position;
  const step = withShift ? 16 : 1;
  let nextX = x;
  let nextY = y;
  switch (key) {
    case 'ArrowLeft':
      nextX = x - step;
      break;
    case 'ArrowRight':
      nextX = x + step;
      break;
    case 'ArrowUp':
      nextY = y - step;
      break;
    case 'ArrowDown':
      nextY = y + step;
      break;
    default:
      throw new Error(`nudge: unknown key "${key}"`);
  }
  return {
    x: Math.max(nextX, 0),
    y: Math.max(nextY, 0),
  };
}


function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function coerceNumeric(raw) {
  if (typeof raw === 'number') {
    return raw;
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    return trimmed === '' ? NaN : Number(trimmed);
  }
  return NaN;
}

const NUMERIC_FIELD_MINIMUMS = { x: 0, y: 0, width: 32, height: 24 };

export function validateElementLayout(raw) {
  if (raw === null || typeof raw !== 'object') {
    return { ok: false, reason: 'layout must be an object' };
  }

  const { x, y, width, height, z, align } = raw;
  const [posMin, posMax] = POSITION_RANGE;
  const [sizeMin, sizeMax] = SIZE_RANGE;
  const [stackMin, stackMax] = STACK_RANGE;

  if (!isFiniteNumber(x) || x < posMin || x > posMax) {
    return { ok: false, reason: `x must be a finite number in ${posMin}..${posMax}` };
  }
  if (!isFiniteNumber(y) || y < posMin || y > posMax) {
    return { ok: false, reason: `y must be a finite number in ${posMin}..${posMax}` };
  }
  if (!isFiniteNumber(width) || width < sizeMin || width > sizeMax) {
    return { ok: false, reason: `width must be a finite number in ${sizeMin}..${sizeMax}` };
  }
  if (!isFiniteNumber(height) || height < sizeMin || height > sizeMax) {
    return { ok: false, reason: `height must be a finite number in ${sizeMin}..${sizeMax}` };
  }
  if (!isFiniteNumber(z) || !Number.isInteger(z) || z < stackMin || z > stackMax) {
    return { ok: false, reason: `z must be an integer in ${stackMin}..${stackMax}` };
  }
  if (align !== undefined && align !== 'left' && align !== 'center' && align !== 'right') {
    return { ok: false, reason: "align must be 'left', or 'center', or 'right'" };
  }

  const value = { x, y, width, height, z };
  if (align !== undefined) {
    value.align = align;
  }
  const isLayoutColor = (color) => typeof color === 'string'
    && color.trim().length > 0
    && color.trim().length <= 64
    && !/[<>{};]/.test(color);
  const isLayoutUrl = (url) => typeof url === 'string'
    && url.trim().length > 0
    && url.trim().length <= 4000
    && !/[<>]/.test(url);
  if (isLayoutColor(raw.backgroundColor)) {
    value.backgroundColor = raw.backgroundColor.trim();
  }
  if (isLayoutColor(raw.textColor)) {
    value.textColor = raw.textColor.trim();
  }
  if (isLayoutUrl(raw.backgroundImage)) {
    value.backgroundImage = raw.backgroundImage.trim();
  }
  if (typeof raw.fontFamily === 'string' && raw.fontFamily.trim() && raw.fontFamily.trim().length <= 160 && !/[<>{}();]/.test(raw.fontFamily)) {
    value.fontFamily = raw.fontFamily.trim();
  }
  if (isFiniteNumber(raw.fontSize) && raw.fontSize >= 6 && raw.fontSize <= 300) {
    value.fontSize = raw.fontSize;
  }
  if ((typeof raw.fontWeight === 'number' && raw.fontWeight >= 100 && raw.fontWeight <= 900)
    || (typeof raw.fontWeight === 'string' && /^(normal|bold|[1-9]00)$/.test(raw.fontWeight))) {
    value.fontWeight = raw.fontWeight;
  }
  if (raw.textAlign === 'left' || raw.textAlign === 'center' || raw.textAlign === 'right' || raw.textAlign === 'justify') {
    value.textAlign = raw.textAlign;
  }
  if (isFiniteNumber(raw.borderRadius) && raw.borderRadius >= 0 && raw.borderRadius <= 9999) {
    value.borderRadius = raw.borderRadius;
  }
  if (typeof raw.src === 'string' && raw.src.trim() && raw.src.length <= 4000 && !/[<>]/.test(raw.src)) {
    value.src = raw.src.trim();
  }
  if (typeof raw.text === 'string' && raw.text.length <= 5000) {
    value.text = raw.text;
  }
  if (raw.hidden === true) {
    value.hidden = true;
  }
  if (typeof raw.geometryEdited === 'boolean') {
    value.geometryEdited = raw.geometryEdited;
  }
  if (raw.geometryAbsolute === true) {
    value.geometryAbsolute = true;
  }
  return { ok: true, value };
}

export function validateReferenceWidth(raw) {
  const [min, max] = REFERENCE_WIDTH_RANGE;
  if (!isFiniteNumber(raw) || !Number.isInteger(raw) || raw < min || raw > max) {
    return { ok: false };
  }
  return { ok: true, value: raw };
}

export function sanitizeNumericInput(field, raw, previousValid) {
  const min = NUMERIC_FIELD_MINIMUMS[field];
  if (min === undefined) {
    return { accepted: false, value: previousValid, error: `unknown field "${field}"` };
  }

  const num = coerceNumeric(raw);
  if (!Number.isFinite(num)) {
    return { accepted: false, value: previousValid, error: `${field} must be a number` };
  }
  if (num < min) {
    return {
      accepted: false,
      value: previousValid,
      error: `${field} must be greater than or equal to ${min}`,
    };
  }

  return { accepted: true, value: num };
}


export function computeScaleFactor(viewportWidth, referenceWidth) {
  if (viewportWidth >= referenceWidth) {
    return 1.0;
  }
  const ratio = viewportWidth / referenceWidth;
  return Math.min(Math.max(ratio, 0.25), 1.0);
}

export function computeResponsiveScaleFactor(viewportWidth, referenceWidth) {
  const vw = Number(viewportWidth);
  const rw = Number(referenceWidth);
  if (!Number.isFinite(vw) || !Number.isFinite(rw) || vw <= 0 || rw <= 0) {
    return 1.0;
  }
  const ratio = vw / rw;
  // Builder and reader coordinates are authored in reference-width units. Large
  // monitors should keep those authored coordinates at 1:1 and use surrounding page
  // whitespace, not upscale every overlay box/offset and create mismatched gaps.
  return Math.min(Math.max(ratio, 0.25), 1.0);
}

export function scaleLayout(elementLayout, scaleFactor) {
  const { x, y, width, height, z, align } = elementLayout;
  const scaled = {
    x: x * scaleFactor,
    y: y * scaleFactor,
    width: width * scaleFactor,
    height: height * scaleFactor,
    z,
  };
  if (align !== undefined) {
    scaled.align = align;
  }
  [
    'backgroundColor',
    'textColor',
    'backgroundImage',
    'fontFamily',
    'fontSize',
    'fontWeight',
    'textAlign',
    'borderRadius',
    'src',
    'text',
    'hidden',
    'geometryEdited',
    'geometryAbsolute',
  ].forEach((key) => {
    if (elementLayout[key] !== undefined) {
      scaled[key] = elementLayout[key];
    }
  });
  return scaled;
}


export function serializeElementLayout(layout) {
  const { x, y, width, height, z, align, backgroundColor, textColor, backgroundImage } = layout;
  const plain = { x, y, width, height, z };
  if (align !== undefined) {
    plain.align = align;
  }
  if (typeof backgroundColor === 'string' && backgroundColor.trim()) {
    plain.backgroundColor = backgroundColor.trim();
  }
  if (typeof textColor === 'string' && textColor.trim()) {
    plain.textColor = textColor.trim();
  }
  if (typeof backgroundImage === 'string' && backgroundImage.trim()) {
    plain.backgroundImage = backgroundImage.trim();
  }
  if (typeof layout.fontFamily === 'string' && layout.fontFamily.trim()) {
    plain.fontFamily = layout.fontFamily.trim();
  }
  if (typeof layout.fontSize === 'number' && Number.isFinite(layout.fontSize)) {
    plain.fontSize = layout.fontSize;
  }
  if (layout.fontWeight !== undefined && layout.fontWeight !== null && layout.fontWeight !== '') {
    plain.fontWeight = layout.fontWeight;
  }
  if (typeof layout.textAlign === 'string' && layout.textAlign) {
    plain.textAlign = layout.textAlign;
  }
  if (typeof layout.borderRadius === 'number' && Number.isFinite(layout.borderRadius) && layout.borderRadius >= 0) {
    plain.borderRadius = layout.borderRadius;
  }
  if (typeof layout.src === 'string' && layout.src.trim()) {
    plain.src = layout.src.trim();
  }
  if (typeof layout.text === 'string') {
    plain.text = layout.text;
  }
  if (layout.hidden === true) {
    plain.hidden = true;
  }
  if (typeof layout.geometryEdited === 'boolean') {
    plain.geometryEdited = layout.geometryEdited;
  }
  if (layout.geometryAbsolute === true) {
    plain.geometryAbsolute = true;
  }
  return plain;
}

export function deserializeElementLayout(plain) {
  return validateElementLayout(plain);
}
